import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getSessionAnalysis } from "../graphql/queries";
import PrintNoteContent from "./PrintNoteContent";

const DEFAULT_REVIEW_OF_SYSTEMS = {
  constitutional: "No fever, chills, weight loss, or fatigue reported.",
  skin: "No rash, itching, or skin lesions noted.",
  ent: "No ear pain, nasal congestion, or sore throat.",
  cardiovascular: "Denies chest pain, palpitations, or edema.",
  respiratory: "No shortness of breath, wheezing, or cough.",
  gastrointestinal: "No nausea, vomiting, diarrhea, or abdominal pain.",
  genitourinary: "No dysuria, hematuria, or urinary frequency.",
  musculoskeletal: "No joint pain, stiffness, or swelling.",
  neurological: "No headaches, dizziness, numbness, or weakness.",
  psychiatric: "No anxiety, depression, or mood changes reported.",
  endocrine: "No polyuria, polydipsia, or heat/cold intolerance.",
  lymphatic: "No swollen lymph nodes or unusual bleeding/bruising.",
  allergicAndImmunologic: "No known allergies or recent infections reported."
};

const formatTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};


const client = generateClient();

export default function PrintSessionResultView({ sessionId, createdAt: createdAtProp }) {
  const { sessionId: routeSessionId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [createdAt, setCreatedAt] = useState(createdAtProp || null);

  const resolvedSessionId = sessionId || routeSessionId;

  useEffect(() => {
    const fetch = async () => {
      const res = await client.graphql({
        query: getSessionAnalysis,
        variables: { id: resolvedSessionId }
      });

      const base = res.data.getSessionAnalysis;
      const parsed = JSON.parse(base?.analysisResult || "{}");
      parsed.diagnoses = parsed.diagnoses || [];
      parsed.addendums = base?.addendums || [];

      if (parsed.noteTemplate !== "BEHAVIOR_THERAPY") {
        for (const key in DEFAULT_REVIEW_OF_SYSTEMS) {
          if (!parsed[key]) parsed[key] = DEFAULT_REVIEW_OF_SYSTEMS[key];
        }
      }

      parsed.signedBy = base?.signedBy || "Unknown";
      parsed.signedAt = base?.signedAt || new Date().toISOString();

      // Include new time metadata
      parsed.timeIn = formatTime(base?.timeIn);
parsed.timeOut = formatTime(base?.timeOut);
      parsed.duration = base?.duration ?? base?.durationMinutes ?? "";
      parsed.isManualTimeEntry = base?.isManualTimeEntry || false;

      setResult(parsed);
      setCreatedAt(base?.createdAt || new Date().toISOString());

      if (!sessionId) {
        setTimeout(() => {
          window.print();
          const goBack = () => {
            window.removeEventListener("afterprint", goBack);
            navigate("/");
          };
          window.addEventListener("afterprint", goBack);
        }, 500);
      }
    };

    if (resolvedSessionId) fetch();
  }, [resolvedSessionId, navigate, sessionId]);

  if (!result) return null;

  return <PrintNoteContent result={result} createdAt={createdAt} />;
}
